import { useState, useEffect } from "react";
import { PositionsApi, type PreviewPositionMarginResponse } from "@nordex/core";
import { useDebounce } from "@/hooks/use-debounce";

interface UseMarginPreviewOptions {
  userId: string | null;
  market: string;
  marginDelta: number;
  enabled?: boolean;
}

interface UseMarginPreviewResult {
  preview: PreviewPositionMarginResponse | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch margin adjustment preview from backend
 * Debounces inputs to prevent excessive API calls
 * 
 * Industry standard: Backend calculates liquidation price authoritatively
 * This includes funding payments and all other factors for accurate preview
 * 
 * @example
 * const { preview, isLoading, error } = useMarginPreview({
 *   userId: "user123",
 *   market: "BTC-USD",
 *   marginDelta: 100,
 *   enabled: true,
 * });
 * 
 * // Display backend-calculated new liquidation price
 * const newLiqPrice = preview?.new_liquidation_price;
 */
export function useMarginPreview(options: UseMarginPreviewOptions): UseMarginPreviewResult {
  const [preview, setPreview] = useState<PreviewPositionMarginResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce marginDelta to avoid excessive API calls (300ms delay)
  const debouncedMarginDelta = useDebounce(options.marginDelta, 300);

  useEffect(() => {
    // Don't fetch if disabled, no user, or no margin change
    if (!options.enabled || !options.userId || debouncedMarginDelta === 0) {
      setPreview(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchPreview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await PositionsApi.previewPositionMargin(
          options.market,
          debouncedMarginDelta
        );
        setPreview(response);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch preview";
        setError(errorMessage);
        setPreview(null);
        console.error("Margin preview error:", errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [
    options.enabled,
    options.userId,
    options.market,
    debouncedMarginDelta,
  ]);

  return { preview, isLoading, error };
}

